import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtNum, fmtDate, aktuellerMonat, downloadCsv, errMsg } from "@/lib/api";
import { MonthPicker } from "@/components/app/MonthPicker";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  FlaskConical, Loader2, Download, Info, TrendingUp, Tractor, Pencil,
  History, ShieldCheck, ShieldAlert, AlertTriangle, ArrowRight, Ban, Check, X,
  Search, ChevronsUpDown,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
} from "recharts";

const BLAU = "hsl(217 72% 42%)";
const CYAN = "hsl(190 90% 36%)";
const GRID = "hsl(213 20% 86%)";
const TICK = { fontSize: 11, fill: "hsl(215 16% 40%)" };

const zellzahlWarn = (v, g) => v > (g?.zellzahl_1 ?? 400000);
const keimzahlWarn = (v, g) => v > (g?.keimzahl_1 ?? 100000);

const fmtTausend = (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v);
const datumKurz = (d) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(2, 4)}`;

const TrendTooltip = ({ active, payload, label, einheit = "", dezimal = 0 }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 shadow-md text-xs">
      <p className="font-medium mb-1">{fmtDate(label)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.stroke }}>
          {p.name}: <span className="font-num font-semibold">{fmtNum(p.value, dezimal)}{einheit}</span>
        </p>
      ))}
    </div>
  );
};

/**
 * Serverseitige Lieferanten-Combobox mit Debounce. Nutzt dieselbe Suche wie die
 * Lieferantenliste (`/api/lieferanten?suche=…`) und liefert die Liefernummer
 * ins Parent-State zurück.
 *
 * Auf Mobile (<sm) wird ein echtes Dialog verwendet – dadurch verhält sich die
 * iOS-Tastatur korrekt (kein Flip nach oben, kein Verdecken des Suchfelds).
 * Auf Desktop (>=sm) bleibt es ein leichtes Popover.
 */
const LieferantSuchCombobox = ({ value, onChange, placeholder = "Lieferant wählen …" }) => {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [istMobile, setIstMobile] = useState(false);

  // Media-Query: sm-Breakpoint (640px) — Mobile-Erkennung
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 639px)");
    const sync = () => setIstMobile(mql.matches);
    sync();
    if (mql.addEventListener) {
      mql.addEventListener("change", sync);
      return () => mql.removeEventListener("change", sync);
    }
    // Fallback für alte Safari-Versionen
    mql.addListener(sync);
    return () => mql.removeListener(sync);
  }, []);

  // Laden mit Debounce (250ms)
  useEffect(() => {
    if (!offen) return;
    const t = setTimeout(() => {
      setLoading(true);
      const params = suche ? { suche } : {};
      api.get("/lieferanten", { params })
        .then((res) => setRows(res.data.slice(0, 50)))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [suche, offen]);

  // Sync-Label falls Value extern gesetzt wird
  useEffect(() => {
    if (!value) { setSelectedLabel(""); return; }
    const match = rows.find((r) => r.liefernummer === value);
    if (match) {
      setSelectedLabel(`${match.liefernummer} · ${match.name}`);
    } else if (!selectedLabel) {
      // einmalig nach initialem Value fetchen
      api.get("/lieferanten", { params: { suche: value } })
        .then((res) => {
          const m = res.data.find((r) => r.liefernummer === value);
          if (m) setSelectedLabel(`${m.liefernummer} · ${m.name}`);
        })
        .catch(() => {});
    }
  }, [value, rows]);

  const trigger = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={offen}
      className="w-full sm:w-[320px] justify-between bg-card font-normal"
      data-testid="trend-supplier-combobox"
      onClick={() => setOffen(true)}
    >
      <span className="truncate">
        {selectedLabel || (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </span>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  const inhalt = (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="Name, Hof, Liefernr. …"
        value={suche}
        onValueChange={setSuche}
        data-testid="trend-supplier-search"
        autoFocus
      />
      <CommandList className="max-h-[60vh] sm:max-h-[320px]">
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-4 justify-center">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Suche …
          </div>
        )}
        {!loading && rows.length === 0 && (
          <CommandEmpty>Kein Lieferant gefunden.</CommandEmpty>
        )}
        <CommandGroup>
          {rows.map((r) => (
            <CommandItem
              key={r.liefernummer}
              value={r.liefernummer}
              onSelect={() => {
                onChange(r.liefernummer);
                setSelectedLabel(`${r.liefernummer} · ${r.name}`);
                setOffen(false);
                setSuche("");
              }}
              data-testid={`trend-supplier-option-${r.liefernummer}`}
            >
              <Check
                className={`mr-2 h-4 w-4 ${value === r.liefernummer ? "opacity-100" : "opacity-0"}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-num font-medium">{r.liefernummer}</span>
                  <span>{r.name}</span>
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {r.hofname} · {r.plz} {r.ort}
                </p>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (istMobile) {
    // Mobile: Dialog — iOS-Tastatur verhält sich korrekt, kein Flip-Problem
    return (
      <>
        {trigger}
        <Dialog open={offen} onOpenChange={(o) => { setOffen(o); if (!o) setSuche(""); }}>
          <DialogContent
            className="p-0 gap-0 max-w-[95vw] sm:max-w-lg top-[10%] translate-y-0"
            data-testid="trend-supplier-dialog"
          >
            <DialogHeader className="sr-only">
              <DialogTitle>Lieferant suchen</DialogTitle>
              <DialogDescription>Nach Name, Hof oder Liefernummer suchen.</DialogDescription>
            </DialogHeader>
            {inhalt}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Desktop: Popover
  return (
    <Popover open={offen} onOpenChange={(o) => { setOffen(o); if (!o) setSuche(""); }}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        {inhalt}
      </PopoverContent>
    </Popover>
  );
};

const TrendSection = ({ liefernummer }) => {
  const [monate, setMonate] = useState("12");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!liefernummer) return;
    setLoading(true);
    api
      .get("/laborwerte/trend", { params: { liefernummer, monate } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [liefernummer, monate]);

  const g = data?.grenzwerte;
  const werte = data?.werte || [];

  if (loading)
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (!werte.length)
    return <EmptyState icon={TrendingUp} title="Keine Laborwerte im gewählten Zeitraum." text="Wählen Sie einen längeren Zeitraum." />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {werte.length} Proben · Grenzwerte gemäß {g?.preismodell === "sortenspezifisch" ? "sortenspezifischem Preismodell" : "Standard-Preismodell"}
        </p>
        <Select value={monate} onValueChange={setMonate}>
          <SelectTrigger data-testid="trend-range-select" className="w-[170px] bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">Letzte 6 Monate</SelectItem>
            <SelectItem value="12">Letzte 12 Monate</SelectItem>
            <SelectItem value="24">Letzte 24 Monate</SelectItem>
            <SelectItem value="36">Letzte 36 Monate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card data-testid="trend-chart-inhaltsstoffe">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Fett & Eiweiß (%)</CardTitle>
          <CardDescription>Referenzwerte (gestrichelt): Fett {fmtNum(g?.ref_fett, 1)} % · Eiweiß {fmtNum(g?.ref_eiweiss, 1)} %</CardDescription>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={werte} margin={{ top: 5, right: 14, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="datum" tickFormatter={datumKurz} tick={TICK} minTickGap={40} />
              <YAxis tick={TICK} width={42} domain={["dataMin - 0.2", "dataMax + 0.2"]} tickFormatter={(v) => v.toFixed(1)} />
              <Tooltip content={<TrendTooltip einheit=" %" dezimal={2} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={g?.ref_fett} stroke={BLAU} strokeDasharray="6 4" strokeOpacity={0.5} />
              <ReferenceLine y={g?.ref_eiweiss} stroke={CYAN} strokeDasharray="6 4" strokeOpacity={0.5} />
              <Line type="monotone" dataKey="fett" name="Fett" stroke={BLAU} strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="eiweiss" name="Eiweiß" stroke={CYAN} strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="trend-chart-zellzahl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Zellzahl</CardTitle>
            <CardDescription>
              Grenzwerte: Klasse S ≤ {fmtNum(g?.zellzahl_s)} · Klasse 1 ≤ {fmtNum(g?.zellzahl_1)}
            </CardDescription>
          </CardHeader>
          <CardContent className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={werte} margin={{ top: 5, right: 14, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="datum" tickFormatter={datumKurz} tick={TICK} minTickGap={40} />
                <YAxis tick={TICK} width={44} tickFormatter={fmtTausend} />
                <Tooltip content={<TrendTooltip />} />
                <ReferenceLine y={g?.zellzahl_s} stroke="hsl(152 45% 32%)" strokeDasharray="6 4"
                  label={{ value: "S", position: "right", fontSize: 11, fill: "hsl(152 45% 32%)" }} />
                <ReferenceLine y={g?.zellzahl_1} stroke="hsl(0 72% 52%)" strokeDasharray="6 4"
                  label={{ value: "Kl. 1", position: "right", fontSize: 11, fill: "hsl(0 72% 52%)" }} />
                <Line type="monotone" dataKey="zellzahl" name="Zellzahl" stroke={CYAN} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="trend-chart-keimzahl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Keimzahl</CardTitle>
            <CardDescription>
              Grenzwerte: Klasse S ≤ {fmtNum(g?.keimzahl_s)} · Klasse 1 ≤ {fmtNum(g?.keimzahl_1)}
            </CardDescription>
          </CardHeader>
          <CardContent className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={werte} margin={{ top: 5, right: 14, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="datum" tickFormatter={datumKurz} tick={TICK} minTickGap={40} />
                <YAxis tick={TICK} width={44} tickFormatter={fmtTausend} />
                <Tooltip content={<TrendTooltip />} />
                <ReferenceLine y={g?.keimzahl_s} stroke="hsl(152 45% 32%)" strokeDasharray="6 4"
                  label={{ value: "S", position: "right", fontSize: 11, fill: "hsl(152 45% 32%)" }} />
                <ReferenceLine y={g?.keimzahl_1} stroke="hsl(0 72% 52%)" strokeDasharray="6 4"
                  label={{ value: "Kl. 1", position: "right", fontSize: 11, fill: "hsl(0 72% 52%)" }} />
                <Line type="monotone" dataKey="keimzahl" name="Keimzahl" stroke={BLAU} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="trend-chart-harnstoff">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Harnstoff (mg/dl)</CardTitle>
            <CardDescription>Zielbereich üblich: 15–30 mg/dl</CardDescription>
          </CardHeader>
          <CardContent className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={werte} margin={{ top: 5, right: 14, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="datum" tickFormatter={datumKurz} tick={TICK} minTickGap={40} />
                <YAxis tick={TICK} width={36} />
                <Tooltip content={<TrendTooltip dezimal={1} />} />
                <ReferenceLine y={15} stroke={GRID} strokeDasharray="6 4" />
                <ReferenceLine y={30} stroke={GRID} strokeDasharray="6 4" />
                <Line type="monotone" dataKey="harnstoff" name="Harnstoff" stroke={CYAN} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="trend-chart-gefrierpunkt">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Gefrierpunkt (°C)</CardTitle>
            <CardDescription>Grenzwert (gesetzlich): ≤ −0,515 °C</CardDescription>
          </CardHeader>
          <CardContent className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={werte} margin={{ top: 5, right: 14, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="datum" tickFormatter={datumKurz} tick={TICK} minTickGap={40} />
                <YAxis tick={TICK} width={56} domain={["dataMin - 0.005", "dataMax + 0.005"]} tickFormatter={(v) => v.toFixed(3)} />
                <Tooltip content={<TrendTooltip dezimal={3} einheit=" °C" />} />
                <ReferenceLine y={-0.515} stroke="hsl(0 72% 52%)" strokeDasharray="6 4"
                  label={{ value: "−0,515", position: "right", fontSize: 11, fill: "hsl(0 72% 52%)" }} />
                <Line type="monotone" dataKey="gefrierpunkt" name="Gefrierpunkt" stroke={BLAU} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default function MilchprobenPage() {
  const { user, liefernummer } = useAuth();
  const [monat, setMonat] = useState(aktuellerMonat());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [grenzwerte, setGrenzwerte] = useState(null);
  const [lieferanten, setLieferanten] = useState([]);
  const [trendNummer, setTrendNummer] = useState("");
  const [systemSettings, setSystemSettings] = useState({ vier_augen_labor: false });
  const [historie, setHistorie] = useState(null);
  const [historieLoading, setHistorieLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectKommentar, setRejectKommentar] = useState("");
  // Suchtext für Proben-Tab (client-seitig getriggert → server-side suche= Param)
  const [suche, setSuche] = useState("");
  const [sucheDebounced, setSucheDebounced] = useState("");

  const istLieferant = user?.rolle === "lieferant";
  const istEditor = user?.rolle === "admin" || user?.rolle === "sysadmin";
  const aktiveTrendNummer = istLieferant ? liefernummer : trendNummer;

  // Debounce Suche 250ms, wie bei Lieferantenseite
  useEffect(() => {
    const t = setTimeout(() => setSucheDebounced(suche.trim()), 250);
    return () => clearTimeout(t);
  }, [suche]);

  const ladeSettings = useCallback(() => {
    if (istEditor) {
      api.get("/einstellungen/system")
        .then((res) => setSystemSettings(res.data))
        .catch(() => setSystemSettings({ vier_augen_labor: false }));
    }
  }, [istEditor]);

  useEffect(() => {
    if (!istLieferant) {
      api.get("/lieferanten").then((res) => {
        setLieferanten(res.data);
        if (res.data.length && !trendNummer) setTrendNummer(res.data[0].liefernummer);
      }).catch(() => {});
    }
  }, [istLieferant, trendNummer]);

  useEffect(() => { ladeSettings(); }, [ladeSettings]);

  const ladeProben = useCallback(() => {
    setLoading(true);
    const params = { monat };
    if (istLieferant && liefernummer) params.liefernummer = liefernummer;
    if (sucheDebounced && !istLieferant) params.suche = sucheDebounced;
    api.get("/laborwerte", { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [monat, liefernummer, istLieferant, sucheDebounced]);

  useEffect(() => { ladeProben(); }, [ladeProben]);

  const ladeHistorie = useCallback(async (lab) => {
    if (!lab?.id) return;
    setHistorieLoading(true);
    try {
      const { data } = await api.get(`/laborwerte/${lab.id}/historie`);
      setHistorie(data);
    } catch (e) {
      setHistorie(null);
    } finally {
      setHistorieLoading(false);
    }
  }, []);

  const ladePendingChanges = useCallback(() => {
    if (!istEditor) return;
    setPendingLoading(true);
    api.get("/lab-changes/pending")
      .then((res) => setPendingChanges(res.data || []))
      .catch(() => setPendingChanges([]))
      .finally(() => setPendingLoading(false));
  }, [istEditor]);

  useEffect(() => { ladePendingChanges(); }, [ladePendingChanges]);

  const freigebenPending = useCallback(async (cr) => {
    try {
      await api.post(`/lab-changes/${cr.id}/approve`);
      toast.success("Änderung freigegeben.");
      ladePendingChanges();
      if (detail?.id === cr.lab_id) ladeHistorie(detail);
      if ((cr.datum || "").startsWith(monat)) ladeProben();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (typeof det === "object" && det?.sperre) {
        toast.error("Freigabe nicht möglich: In der Zwischenzeit wurde eine Abrechnung erzeugt.");
      } else {
        toast.error(errMsg(e));
      }
    }
  }, [detail, monat, ladeProben, ladeHistorie, ladePendingChanges]);

  const ablehnenPending = useCallback((cr) => {
    setRejectingId(cr.id);
    setRejectKommentar("");
  }, []);

  const ablehnenBestaetigen = useCallback(async () => {
    if (!rejectingId) return;
    try {
      await api.post(`/lab-changes/${rejectingId}/reject`, { kommentar: rejectKommentar });
      toast.success("Änderung abgelehnt.");
      ladePendingChanges();
      setRejectingId(null);
      setRejectKommentar("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [rejectingId, rejectKommentar, ladePendingChanges]);

  useEffect(() => {
    if (!aktiveTrendNummer) return;
    api.get("/laborwerte/trend", { params: { liefernummer: aktiveTrendNummer, monate: 3 } })
      .then((res) => setGrenzwerte(res.data.grenzwerte))
      .catch(() => {});
  }, [aktiveTrendNummer]);

  const exportCsv = () =>
    downloadCsv(
      `Milchproben_${monat}.csv`,
      ["Datum", "Probenummer", "Liefernummer", "Fett %", "Eiweiß %", "Zellzahl", "Keimzahl", "Hemmstoff", "Gefrierpunkt", "Harnstoff"],
      rows.map((r) => [r.datum, r.probenummer, r.liefernummer, r.fett, r.eiweiss, r.zellzahl, r.keimzahl, r.hemmstoff ? "positiv" : "negativ", r.gefrierpunkt ?? "", r.harnstoff ?? ""])
    );

  const grenzTabelle = [
    { merkmal: "Keimzahl (geom. Mittel 2 Monate)", s: `≤ ${fmtNum(grenzwerte?.keimzahl_s ?? 50000)}`, k1: `≤ ${fmtNum(grenzwerte?.keimzahl_1 ?? 100000)}`, k2: `> ${fmtNum(grenzwerte?.keimzahl_1 ?? 100000)}` },
    { merkmal: "Zellzahl (geom. Mittel 3 Monate)", s: `≤ ${fmtNum(grenzwerte?.zellzahl_s ?? 250000)}`, k1: `≤ ${fmtNum(grenzwerte?.zellzahl_1 ?? 400000)}`, k2: `> ${fmtNum(grenzwerte?.zellzahl_1 ?? 400000)}` },
    { merkmal: "Hemmstoff", s: "negativ", k1: "negativ", k2: "Abzug bei positivem Befund" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Milchproben</h1>
          <p className="text-sm text-muted-foreground">
            {istLieferant ? `Liefernummer ${liefernummer}` : "Alle Lieferanten"} · Laborwerte, Grenzwerte & Langzeit-Trends
          </p>
        </div>
      </div>

      <Tabs defaultValue="proben">
        <TabsList className="w-full sm:w-auto justify-start overflow-x-auto h-auto p-1">
          <TabsTrigger data-testid="samples-tab-table" value="proben" className="flex-1 sm:flex-none">
            <FlaskConical className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Proben</span>
            <span className="sm:hidden ml-1.5 text-xs">Proben</span>
          </TabsTrigger>
          <TabsTrigger data-testid="samples-tab-trends" value="trends" className="flex-1 sm:flex-none">
            <TrendingUp className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Langzeit-Trends</span>
            <span className="sm:hidden ml-1.5 text-xs">Trends</span>
          </TabsTrigger>
          {istEditor && (
            <TabsTrigger data-testid="samples-tab-freigaben" value="freigaben" className="flex-1 sm:flex-none relative">
              <ShieldAlert className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Ausstehende Freigaben</span>
              <span className="sm:hidden ml-1.5 text-xs">Freigaben</span>
              {pendingChanges.length > 0 && (
                <Badge variant="destructive" className="ml-1.5 h-4 text-[10px] px-1.5" data-testid="pending-count-badge">
                  {pendingChanges.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="proben" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            {!istLieferant && (
              <div className="relative flex-1 min-w-[180px] sm:flex-none">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="samples-search-input"
                  className="pl-8 sm:w-[260px] bg-card"
                  placeholder="Name, Hof, Liefernr., Probenr. …"
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                />
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              <MonthPicker value={monat} onChange={setMonat} />
              <Button data-testid="samples-csv-export-button" variant="secondary" onClick={exportCsv} disabled={!rows.length}>
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">CSV</span>
              </Button>
            </div>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : rows.length ? (
                <>
                  {/* Mobile: Card-Liste (< md) */}
                  <div className="md:hidden divide-y" data-testid="milk-samples-mobile">
                    {rows.map((r) => {
                      const zzWarn = zellzahlWarn(r.zellzahl, grenzwerte);
                      const kzWarn = keimzahlWarn(r.keimzahl, grenzwerte);
                      return (
                        <div key={r.id} className="p-3 hover:bg-secondary/40 transition-colors">
                          <button
                            onClick={() => { setDetail(r); ladeHistorie(r); }}
                            data-testid={`sample-mobile-${r.probenummer}`}
                            className="w-full text-left"
                          >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{fmtDate(r.datum)}</span>
                                {r.hemmstoff && <Badge variant="destructive" className="text-[10px]">Hemmstoff positiv</Badge>}
                                {(zzWarn || kzWarn) && !r.hemmstoff && (
                                  <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">Grenzwert</Badge>
                                )}
                                {r.zuletzt_bearbeitet_am && (
                                  <Badge variant="outline" className="text-[10px]">bearbeitet</Badge>
                                )}
                              </div>
                              {!istLieferant && (r.lieferant_name || r.hofname) && (
                                <p className="text-xs text-foreground/80 mt-0.5 truncate">
                                  <Tractor className="inline h-3 w-3 mr-1 text-muted-foreground" />
                                  {r.lieferant_name}
                                  {r.hofname && <span className="text-muted-foreground"> · {r.hofname}</span>}
                                </p>
                              )}
                              <p className="text-[11px] text-muted-foreground font-num truncate">
                                Probe {r.probenummer}
                                {!istLieferant && <> · Nr. {r.liefernummer}</>}
                              </p>
                            </div>
                            <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                          </div>
                          <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                            <div>
                              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Fett</p>
                              <p className="text-sm font-num font-semibold">{fmtNum(r.fett, 2)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Eiweiß</p>
                              <p className="text-sm font-num font-semibold">{fmtNum(r.eiweiss, 2)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Zellzahl</p>
                              <p className={`text-sm font-num font-semibold ${zzWarn ? "text-destructive" : ""}`}>
                                {fmtTausend(r.zellzahl)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Keimzahl</p>
                              <p className={`text-sm font-num font-semibold ${kzWarn ? "text-destructive" : ""}`}>
                                {fmtTausend(r.keimzahl)}
                              </p>
                            </div>
                          </div>
                          </button>
                          {istEditor && (
                            <div className="mt-2 flex justify-end">
                              <Button
                                data-testid={`sample-mobile-edit-${r.probenummer}`}
                                variant="outline"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                              >
                                <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop/Tablet: Tabelle (>= md) */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table data-testid="milk-samples-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-card">Probenahme</TableHead>
                          <TableHead>Probe-ID</TableHead>
                          {!istLieferant && <TableHead>Liefernr.</TableHead>}
                          {!istLieferant && <TableHead>Landwirt</TableHead>}
                          <TableHead className="text-right">Fett %</TableHead>
                          <TableHead className="text-right">Eiweiß %</TableHead>
                          <TableHead className="text-right">Zellzahl</TableHead>
                          <TableHead className="text-right">Keimzahl</TableHead>
                          <TableHead>Hemmstoff</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="sticky left-0 bg-card font-medium">{fmtDate(r.datum)}</TableCell>
                            <TableCell className="font-num text-xs">{r.probenummer}</TableCell>
                            {!istLieferant && <TableCell className="font-num">{r.liefernummer}</TableCell>}
                            {!istLieferant && (
                              <TableCell className="text-sm max-w-[220px]">
                                <div className="truncate">{r.lieferant_name || "–"}</div>
                                {r.hofname && (
                                  <div className="text-[11px] text-muted-foreground truncate">{r.hofname}</div>
                                )}
                              </TableCell>
                            )}
                            <TableCell className="text-right font-num">{fmtNum(r.fett, 2)}</TableCell>
                            <TableCell className="text-right font-num">{fmtNum(r.eiweiss, 2)}</TableCell>
                            <TableCell className={`text-right font-num ${zellzahlWarn(r.zellzahl, grenzwerte) ? "text-destructive font-semibold" : ""}`}>
                              {fmtNum(r.zellzahl)}
                            </TableCell>
                            <TableCell className={`text-right font-num ${keimzahlWarn(r.keimzahl, grenzwerte) ? "text-destructive font-semibold" : ""}`}>
                              {fmtNum(r.keimzahl)}
                            </TableCell>
                            <TableCell>
                              {r.hemmstoff ? <Badge variant="destructive">positiv</Badge> : <span className="text-xs text-muted-foreground">negativ</span>}
                            </TableCell>
                            <TableCell>
                              {r.zuletzt_bearbeitet_am && (
                                <Badge variant="outline" className="mr-1 text-[10px]" title={`Zuletzt geändert am ${fmtDate(r.zuletzt_bearbeitet_am)} von ${r.zuletzt_bearbeitet_von || ""}`}>
                                  bearbeitet
                                </Badge>
                              )}
                              <Button data-testid={`sample-detail-button-${r.probenummer}`} variant="ghost" size="icon" onClick={() => { setDetail(r); ladeHistorie(r); }}>
                                <Info className="h-4 w-4" />
                              </Button>
                              {istEditor && (
                                <Button data-testid={`sample-edit-button-${r.probenummer}`} variant="ghost" size="icon" onClick={() => setEditing(r)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <EmptyState icon={FlaskConical} title="Keine Proben für den gewählten Monat." />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4 mt-4">
          {!istLieferant && (
            <div className="flex flex-wrap items-center gap-2">
              <Tractor className="h-4 w-4 text-muted-foreground shrink-0" />
              <LieferantSuchCombobox
                value={trendNummer}
                onChange={setTrendNummer}
              />
            </div>
          )}
          {aktiveTrendNummer ? (
            <TrendSection liefernummer={aktiveTrendNummer} />
          ) : (
            <EmptyState icon={TrendingUp} title="Bitte einen Lieferanten wählen." />
          )}
        </TabsContent>

        {istEditor && (
          <TabsContent value="freigaben" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-primary" /> Ausstehende Freigaben (4-Augen-Prinzip)
                </CardTitle>
                <CardDescription>
                  Änderungen an Probenwerten, die auf die Freigabe durch einen zweiten Administrator warten.
                  Selbstgenehmigung wird verhindert. Sobald bereits eine Abrechnung existiert, ist die
                  Freigabe gesperrt (Storno erforderlich).
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                    <Loader2 className="h-4 w-4 animate-spin" /> Lade offene Anfragen …
                  </div>
                ) : pendingChanges.length === 0 ? (
                  <EmptyState
                    icon={ShieldCheck}
                    title="Alles freigegeben."
                    description="Aktuell warten keine Änderungen auf Freigabe."
                  />
                ) : (
                  <div className="space-y-3">
                    {pendingChanges.map((cr) => {
                      const eigen = cr.erstellt_von_id === user?.id;
                      return (
                        <div
                          key={cr.id}
                          data-testid={`freigaben-item-${cr.id}`}
                          className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 sm:p-4 space-y-3"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-[10px] uppercase">
                                  {cr.probenummer}
                                </Badge>
                                <span className="text-sm font-medium">
                                  Lieferant {cr.liefernummer}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  · Probendatum {fmtDate(cr.datum)}
                                </span>
                              </div>
                              <p className="text-[11px] text-muted-foreground mt-1">
                                Beantragt {fmtDate(cr.erstellt_am)} von <strong>{cr.erstellt_von}</strong>
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                const lab = await api.get(`/laborwerte/${cr.lab_id}/historie`)
                                  .then((r) => ({ id: r.data.lab_id, probenummer: r.data.probenummer,
                                    liefernummer: r.data.liefernummer, datum: r.data.datum }))
                                  .catch(() => null);
                                if (lab) { setDetail(lab); ladeHistorie(lab); }
                              }}
                              data-testid={`freigaben-detail-${cr.id}`}
                            >
                              <Info className="h-3.5 w-3.5 mr-1" /> Details
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {Object.entries(cr.aenderungen || {}).map(([feld, w]) => (
                              <div
                                key={feld}
                                className="flex items-center gap-2 rounded bg-background px-2 py-1.5 text-xs"
                              >
                                <span className="font-medium uppercase tracking-wide text-[10px] w-24 shrink-0">
                                  {feld}
                                </span>
                                <span className="font-num text-destructive line-through">
                                  {String(w.alt ?? "—")}
                                </span>
                                <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                <span className="font-num text-primary font-semibold">
                                  {String(w.neu ?? "—")}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="rounded bg-background/60 border border-border/60 p-2 text-xs">
                            <span className="text-muted-foreground">Grund: </span>
                            <span className="italic">{cr.grund}</span>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {eigen ? (
                              <span className="text-[11px] text-muted-foreground italic flex items-center gap-1">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Sie haben diese Änderung beantragt – ein zweiter Administrator muss freigeben.
                              </span>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => freigebenPending(cr)}
                                  data-testid={`freigaben-approve-${cr.id}`}
                                >
                                  <Check className="h-3.5 w-3.5 mr-1" /> Freigeben
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => ablehnenPending(cr)}
                                  data-testid={`freigaben-reject-${cr.id}`}
                                >
                                  <X className="h-3.5 w-3.5 mr-1" /> Ablehnen
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Ablehnungs-Dialog mit Kommentar */}
      <Dialog open={!!rejectingId} onOpenChange={(o) => !o && setRejectingId(null)}>
        <DialogContent data-testid="reject-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Änderung ablehnen</DialogTitle>
            <DialogDescription>
              Der Antragsteller sieht Ihren Kommentar. Sie können auch ohne Kommentar ablehnen.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Kommentar (optional)</Label>
            <Textarea
              value={rejectKommentar}
              onChange={(e) => setRejectKommentar(e.target.value)}
              placeholder="z. B. Bitte um Rücksprache mit Labor"
              rows={3}
              data-testid="reject-kommentar"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectingId(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={ablehnenBestaetigen} data-testid="reject-confirm">
              <X className="h-4 w-4 mr-2" /> Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && (setDetail(null), setHistorie(null))}>
        <DialogContent data-testid="sample-detail-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Probe {detail?.probenummer}</DialogTitle>
            <DialogDescription>
              {fmtDate(detail?.datum)} · Liefernummer {detail?.liefernummer}
              {detail?.zuletzt_bearbeitet_am && (
                <span className="block text-xs text-primary mt-1">
                  Zuletzt geändert am {fmtDate(detail.zuletzt_bearbeitet_am)} von {detail.zuletzt_bearbeitet_von}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Fett</p><p className="font-num font-semibold">{fmtNum(detail.fett, 2)} %</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Eiweiß</p><p className="font-num font-semibold">{fmtNum(detail.eiweiss, 2)} %</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Zellzahl</p><p className="font-num font-semibold">{fmtNum(detail.zellzahl)}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Keimzahl</p><p className="font-num font-semibold">{fmtNum(detail.keimzahl)}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Gefrierpunkt</p><p className="font-num font-semibold">{fmtNum(detail.gefrierpunkt, 3)} °C</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Harnstoff</p><p className="font-num font-semibold">{fmtNum(detail.harnstoff, 1)} mg/dl</p></div>
              </div>

              {/* Historie-Sektion (nur für Admins & wenn Historie vorhanden) */}
              {istEditor && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                    <History className="h-4 w-4" /> Änderungshistorie
                  </p>
                  {historieLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Historie wird geladen …
                    </div>
                  ) : historie?.historie?.length ? (
                    <div className="space-y-2">
                      {historie.historie.slice().reverse().map((h, idx) => (
                        <div key={idx} data-testid={`historie-eintrag-${idx}`} className="rounded-lg border bg-secondary/40 p-3 text-xs space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={h.modus === "vier_augen" ? "default" : "outline"} className="text-[10px]">
                              {h.modus === "vier_augen" ? "4-Augen" : "Direktänderung"}
                            </Badge>
                            <span className="text-muted-foreground">
                              {fmtDate(h.geaendert_am)} · {h.geaendert_von}
                            </span>
                            {h.freigegeben_von && (
                              <span className="text-muted-foreground">
                                · freigegeben durch {h.freigegeben_von}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {Object.entries(h.aenderungen || {}).map(([feld, w]) => (
                              <div key={feld} className="flex items-center gap-2 rounded bg-background px-2 py-1">
                                <span className="font-medium uppercase tracking-wide text-[10px] w-20 shrink-0">{feld}</span>
                                <span className="font-num text-destructive line-through">{String(w.alt ?? "—")}</span>
                                <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                <span className="font-num text-primary font-semibold">{String(w.neu ?? "—")}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-muted-foreground italic">Grund: {h.grund}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Keine Änderungen protokolliert.</p>
                  )}
                  {historie?.change_requests?.some((cr) => cr.status === "offen") && (
                    <div className="mt-2 space-y-2">
                      {historie.change_requests.filter((cr) => cr.status === "offen").map((cr) => {
                        const eigen = cr.erstellt_von_id === user?.id;
                        return (
                          <div
                            key={cr.id}
                            data-testid={`pending-change-${cr.id}`}
                            className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs space-y-2"
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <ShieldAlert className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                              <span className="font-medium text-amber-700 dark:text-amber-400">
                                Ausstehende Änderung
                              </span>
                              <span className="text-muted-foreground">
                                · {fmtDate(cr.erstellt_am)} · beantragt von {cr.erstellt_von}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {Object.entries(cr.aenderungen || {}).map(([feld, w]) => (
                                <div key={feld} className="flex items-center gap-2 rounded bg-background px-2 py-1">
                                  <span className="font-medium uppercase tracking-wide text-[10px] w-20 shrink-0">{feld}</span>
                                  <span className="font-num text-destructive line-through">{String(w.alt ?? "—")}</span>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="font-num text-primary font-semibold">{String(w.neu ?? "—")}</span>
                                </div>
                              ))}
                            </div>
                            <p className="text-muted-foreground italic">Grund: {cr.grund}</p>
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                              {eigen ? (
                                <span className="text-[11px] text-muted-foreground italic">
                                  Sie haben diese Änderung beantragt – ein zweiter Administrator muss freigeben.
                                </span>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    data-testid={`pending-approve-${cr.id}`}
                                    onClick={() => freigebenPending(cr)}
                                  >
                                    <Check className="h-3.5 w-3.5 mr-1" /> Freigeben
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid={`pending-reject-${cr.id}`}
                                    onClick={() => ablehnenPending(cr)}
                                  >
                                    <X className="h-3.5 w-3.5 mr-1" /> Ablehnen
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div>
                <p className="text-sm font-medium mb-2">Grenzwerte (anwendbares Preismodell)</p>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Merkmal</TableHead>
                        <TableHead>Klasse S</TableHead>
                        <TableHead>Klasse 1</TableHead>
                        <TableHead>Klasse 2</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grenzTabelle.map((gz) => (
                        <TableRow key={gz.merkmal}>
                          <TableCell className="text-xs">{gz.merkmal}</TableCell>
                          <TableCell className="text-xs font-num">{gz.s}</TableCell>
                          <TableCell className="text-xs font-num">{gz.k1}</TableCell>
                          <TableCell className="text-xs font-num">{gz.k2}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              {istEditor && (
                <DialogFooter>
                  <Button data-testid="sample-detail-edit-button" onClick={() => { setEditing(detail); }}>
                    <Pencil className="h-4 w-4 mr-2" /> Werte bearbeiten
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <LabEditDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        lab={editing}
        vierAugenAktiv={systemSettings.vier_augen_labor}
        onSaved={() => {
          ladeProben();
          ladePendingChanges();
          if (detail?.id === editing?.id) ladeHistorie(editing);
          setEditing(null);
        }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// LabEditDialog – Bearbeitung der Probenwerte
// ---------------------------------------------------------------------------
const EDIT_FIELDS = [
  { key: "fett", label: "Fett %", step: "0.01", suffix: "%" },
  { key: "eiweiss", label: "Eiweiß %", step: "0.01", suffix: "%" },
  { key: "zellzahl", label: "Zellzahl", step: "1", suffix: "" },
  { key: "keimzahl", label: "Keimzahl", step: "1", suffix: "" },
  { key: "gefrierpunkt", label: "Gefrierpunkt °C", step: "0.001", suffix: "°C" },
  { key: "harnstoff", label: "Harnstoff mg/dl", step: "0.1", suffix: "mg/dl" },
];

const LabEditDialog = ({ open, onClose, lab, vierAugenAktiv, onSaved }) => {
  const [werte, setWerte] = useState({});
  const [grund, setGrund] = useState("");
  const [saving, setSaving] = useState(false);
  const [sperre, setSperre] = useState(null);

  useEffect(() => {
    if (open && lab) {
      const init = {};
      for (const f of EDIT_FIELDS) init[f.key] = lab[f.key] ?? "";
      init.hemmstoff = !!lab.hemmstoff;
      setWerte(init);
      setGrund("");
      setSperre(null);
    }
  }, [open, lab]);

  if (!open || !lab) return null;

  const geaendert = EDIT_FIELDS.some((f) => {
    const alt = lab[f.key];
    const neu = werte[f.key];
    if (neu === "" || neu === null || neu === undefined) return false;
    return Number(neu) !== Number(alt);
  }) || (werte.hemmstoff !== !!lab.hemmstoff);

  const speichern = async () => {
    if (grund.trim().length < 3) {
      toast.error("Bitte einen Grund angeben (mind. 3 Zeichen).");
      return;
    }
    if (!geaendert) {
      toast.error("Keine Änderungen erkannt.");
      return;
    }
    const payload = { grund: grund.trim() };
    for (const f of EDIT_FIELDS) {
      const neu = werte[f.key];
      if (neu === "" || neu === null || neu === undefined) continue;
      const num = Number(neu);
      if (Number.isFinite(num) && num !== Number(lab[f.key])) payload[f.key] = num;
    }
    if (werte.hemmstoff !== !!lab.hemmstoff) payload.hemmstoff = !!werte.hemmstoff;

    setSaving(true);
    try {
      const { data } = await api.put(`/laborwerte/${lab.id}`, payload);
      if (data.status === "pending") {
        toast.success("Änderung wartet auf Freigabe durch einen zweiten Administrator.");
      } else {
        toast.success("Änderungen gespeichert.");
      }
      onSaved?.();
    } catch (e) {
      // 409 → Sperre
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof det === "object" && det?.sperre) {
        setSperre(det.sperre);
        toast.error("Bearbeitung gesperrt: Abrechnung existiert bereits.");
      } else {
        toast.error(errMsg(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="sample-edit-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Probe {lab.probenummer} bearbeiten
          </DialogTitle>
          <DialogDescription>
            {fmtDate(lab.datum)} · Liefernummer {lab.liefernummer}
          </DialogDescription>
        </DialogHeader>

        {vierAugenAktiv && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-xs flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-primary">4-Augen-Prinzip aktiv</p>
              <p className="text-muted-foreground mt-0.5">
                Ihre Änderung wird als Vorschlag gespeichert und muss von einem zweiten Administrator freigegeben werden, bevor sie wirksam wird.
              </p>
            </div>
          </div>
        )}

        {sperre && (
          <div data-testid="sample-edit-sperre" className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs flex items-start gap-2">
            <Ban className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-destructive">Bearbeitung gesperrt</p>
              <p className="text-muted-foreground mt-0.5">
                Für {sperre.liefernummer} · Monat {sperre.monat} existiert bereits eine Abrechnung
                (Status: {sperre.status?.join(", ")}). Bitte zuerst die Abrechnung stornieren.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {EDIT_FIELDS.map((f) => {
            const alt = lab[f.key];
            const neu = werte[f.key];
            const veraendert = neu !== "" && neu !== null && Number(neu) !== Number(alt);
            return (
              <div key={f.key}>
                <Label className="text-xs flex items-center justify-between">
                  <span>{f.label}</span>
                  {veraendert && (
                    <span className="text-[10px] text-muted-foreground font-num">
                      alt: {String(alt)}
                    </span>
                  )}
                </Label>
                <Input
                  type="number"
                  step={f.step}
                  value={neu ?? ""}
                  onChange={(e) => setWerte({ ...werte, [f.key]: e.target.value })}
                  data-testid={`edit-input-${f.key}`}
                  className={veraendert ? "border-primary" : ""}
                />
              </div>
            );
          })}
          <div className="col-span-full flex items-center gap-3 rounded-lg border p-3">
            <Switch
              id="edit-hemmstoff"
              checked={!!werte.hemmstoff}
              onCheckedChange={(v) => setWerte({ ...werte, hemmstoff: v })}
              data-testid="edit-input-hemmstoff"
            />
            <Label htmlFor="edit-hemmstoff" className="text-sm">
              Hemmstoff positiv
              {werte.hemmstoff !== !!lab.hemmstoff && (
                <span className="ml-2 text-[10px] text-muted-foreground">
                  alt: {lab.hemmstoff ? "positiv" : "negativ"}
                </span>
              )}
            </Label>
          </div>
        </div>

        <div>
          <Label className="text-xs">Grund der Änderung <span className="text-destructive">*</span></Label>
          <Textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            placeholder="z. B. Nachmessung Labor bestätigt geänderten Fettwert"
            rows={3}
            data-testid="edit-input-grund"
            className={grund.trim().length < 3 ? "border-destructive/60" : ""}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Pflichtfeld · mind. 3 Zeichen · wird in der Änderungshistorie protokolliert.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button
            onClick={speichern}
            disabled={saving || grund.trim().length < 3 || !geaendert || !!sperre}
            data-testid="sample-edit-save-button"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (
              vierAugenAktiv ? <ShieldCheck className="h-4 w-4 mr-2" /> : <Check className="h-4 w-4 mr-2" />
            )}
            {vierAugenAktiv ? "Änderung einreichen" : "Änderungen speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
